import axios from 'axios';

function envelopeItem(data) {
  return data?.response?.[0];
}

export function fetchAthleteHome() {
  return axios.get('/athlete-home/').then(({ data }) => envelopeItem(data));
}

function progressRangeBody({ startDate, endDate, athleteId } = {}) {
  const body = {};
  if (startDate) body.start_date = startDate;
  if (endDate) body.end_date = endDate;
  if (athleteId) body.athlete_id = athleteId;
  return body;
}

export function fetchAthleteProgressOverview(range = {}) {
  return axios
    .post('/retrieve-athlete-progress-overview/', progressRangeBody(range))
    .then(({ data }) => envelopeItem(data));
}

export function fetchAthleteProgressStrength(range = {}) {
  return axios
    .post('/retrieve-athlete-progress-strength/', progressRangeBody(range))
    .then(({ data }) => envelopeItem(data));
}

export function fetchAthleteProgressVolume(range = {}) {
  return axios
    .post('/retrieve-athlete-progress-volume/', progressRangeBody(range))
    .then(({ data }) => envelopeItem(data));
}

export function fetchAthleteProgressCompliance(range = {}) {
  return axios
    .post('/retrieve-athlete-progress-compliance/', progressRangeBody(range))
    .then(({ data }) => envelopeItem(data));
}

export function fetchWorkoutSessionLiveSummary({
  workoutSessionId,
  athleteId,
} = {}) {
  const body = {};
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  if (athleteId) body.athlete_id = athleteId;
  return axios
    .post('/retrieve-workout-session-live-summary/', body)
    .then(({ data }) => envelopeItem(data)?.live ?? null);
}

export function fetchAthleteAchievements(athleteId) {
  return axios
    .get(`/athletes/${athleteId}/achievements/`)
    .then(({ data }) => (Array.isArray(data?.response) ? data.response : []));
}

export function heartbeatWorkoutSession(workoutSessionId) {
  return axios
    .post(`/workout-sessions/${workoutSessionId}/heartbeat/`, {})
    .then(({ data }) => envelopeItem(data));
}

export function postHeartRateSamples(workoutSessionId, samples) {
  return axios
    .post(`/workout-sessions/${workoutSessionId}/heart-rate-samples/`, {
      samples,
    })
    .then(({ data }) => envelopeItem(data));
}

export function abandonWorkoutSession(workoutSessionId) {
  return axios
    .post(`/workout-sessions/${workoutSessionId}/abandon/`, {})
    .then(({ data }) => envelopeItem(data));
}

export function fetchAthleteCalendar({ fromDate, toDate }) {
  return axios
    .post('/retrieve-calendar/', {
      from_date: fromDate,
      to_date: toDate,
    })
    .then(({ data }) => {
      const payload = envelopeItem(data);
      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        total: payload?.total ?? 0,
      };
    });
}

export function fetchWorkoutSessions({ page = 0, pageSize = 25 } = {}) {
  return axios
    .post('/retrieve-workout-sessions/', {
      page,
      page_size: pageSize,
    })
    .then(({ data }) => {
      const payload = envelopeItem(data);
      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        total: payload?.total ?? 0,
        page_count: payload?.page_count ?? 0,
      };
    });
}

export function fetchNextWorkout() {
  return axios.get('/next-workout/').then(({ data }) => envelopeItem(data));
}

export function fetchStartableWorkouts({ onDate } = {}) {
  const body = {};
  if (onDate) body.on_date = onDate;
  return axios.post('/retrieve-startable-workouts/', body).then(({ data }) => {
    const payload = envelopeItem(data);
    return {
      on_date: payload?.on_date,
      data: Array.isArray(payload?.data) ? payload.data : [],
      total: payload?.total ?? 0,
    };
  });
}

export function loadWorkout({
  source,
  workoutSlotId,
  athleteCalendarEventId,
  workoutSessionId,
  onDate,
} = {}) {
  const body = { source };
  if (workoutSlotId) body.workout_slot_id = workoutSlotId;
  if (athleteCalendarEventId) {
    body.athlete_calendar_event_id = athleteCalendarEventId;
  }
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  if (onDate) body.on_date = onDate;
  return axios
    .post('/load-workout/', body)
    .then(({ data }) => envelopeItem(data));
}

export function startCustomWorkoutSession() {
  return axios
    .post('/custom-workout-sessions/', {})
    .then(({ data }) => envelopeItem(data));
}

export function addExerciseToWorkoutSession(
  workoutSessionId,
  { exerciseId, sets, reps, restSeconds },
) {
  const body = {
    exercise_id: exerciseId,
    sets,
    reps,
  };
  if (restSeconds != null) body.rest_seconds = restSeconds;
  return axios
    .post(`/workout-sessions/${workoutSessionId}/add-exercise/`, body)
    .then(({ data }) => envelopeItem(data));
}

export function completeWorkoutSession(workoutSessionId) {
  return axios
    .post(`/workout-sessions/${workoutSessionId}/complete/`, {})
    .then(({ data }) => envelopeItem(data));
}

export function retrieveExercises({
  name = '',
  page = 0,
  pageSize = 25,
} = {}) {
  return axios
    .post('/retrieve-exercises/', {
      name,
      page,
      page_size: pageSize,
    })
    .then(({ data }) => {
      const payload = envelopeItem(data);
      return {
        page_count: payload?.page_count ?? 0,
        data: Array.isArray(payload?.data) ? payload.data : [],
        total: payload?.total ?? 0,
      };
    });
}

export function startSlotSession(workoutSlotId) {
  return axios
    .post(`/workout-slots/${workoutSlotId}/start-session/`, {})
    .then(({ data }) => envelopeItem(data));
}

export function startOverlaySession(athleteCalendarEventId, performedOn) {
  const body = {};
  if (performedOn) body.performed_on = performedOn;
  return axios
    .post(
      `/athlete-calendar-events/${athleteCalendarEventId}/start-session/`,
      body,
    )
    .then(({ data }) => envelopeItem(data));
}

export function skipWorkoutSlot(workoutSlotId) {
  return axios
    .post(`/workout-slots/${workoutSlotId}/skip/`, {})
    .then(({ data }) => envelopeItem(data));
}

export function startSetAttempt({
  workoutSlotId,
  workoutSessionId,
  plannedSetId,
}) {
  const body = { planned_set_id: plannedSetId };
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  else body.workout_slot_id = workoutSlotId;
  return axios
    .post('/set-attempts/start/', body)
    .then(({ data }) => envelopeItem(data));
}

export function completeSetAttempt({
  workoutSlotId,
  workoutSessionId,
  plannedSetId,
  setAttemptId,
  actualWeight,
  actualReps,
  actualRpe,
}) {
  const body = {
    actual_weight: actualWeight,
    actual_reps: actualReps,
    actual_rpe: actualRpe,
  };
  if (setAttemptId) {
    return axios
      .post(`/set-attempts/${setAttemptId}/complete/`, body)
      .then(({ data }) => envelopeItem(data));
  }
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  else body.workout_slot_id = workoutSlotId;
  body.planned_set_id = plannedSetId;
  return axios
    .post('/set-attempts/complete/', body)
    .then(({ data }) => envelopeItem(data));
}

export function skipSetAttempt({
  workoutSlotId,
  workoutSessionId,
  plannedSetId,
}) {
  const body = { planned_set_id: plannedSetId };
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  else body.workout_slot_id = workoutSlotId;
  return axios
    .post('/set-attempts/skip/', body)
    .then(({ data }) => envelopeItem(data));
}

export function unskipSetAttempt({
  workoutSlotId,
  workoutSessionId,
  plannedSetId,
  setAttemptId,
}) {
  const body = { planned_set_id: plannedSetId };
  if (setAttemptId) body.set_attempt_id = setAttemptId;
  if (workoutSessionId) body.workout_session_id = workoutSessionId;
  else if (!setAttemptId) body.workout_slot_id = workoutSlotId;
  return axios
    .post('/set-attempts/unskip/', body)
    .then(({ data }) => envelopeItem(data));
}

export function extraRestSetAttempt(setAttemptId, seconds = 10) {
  return axios
    .post(`/set-attempts/${setAttemptId}/extra-rest/`, { seconds })
    .then(({ data }) => envelopeItem(data));
}

export function startRestAttempt(setAttemptId, seconds) {
  return axios
    .post(`/set-attempts/${setAttemptId}/start-rest/`, { seconds })
    .then(({ data }) => envelopeItem(data));
}

export function endRestAttempt(setAttemptId, { clear = false } = {}) {
  return axios
    .post(`/set-attempts/${setAttemptId}/end-rest/`, { clear })
    .then(({ data }) => envelopeItem(data));
}

export function restRemainingFromEndsAt(restEndsAt) {
  if (!restEndsAt) return 0;
  const endsMs = Date.parse(restEndsAt);
  if (!Number.isFinite(endsMs)) return 0;
  return Math.max(0, Math.floor((endsMs - Date.now()) / 1000));
}

export function restTotalFromWindow(restStartedAt, restEndsAt) {
  if (!restStartedAt || !restEndsAt) return 0;
  const startMs = Date.parse(restStartedAt);
  const endsMs = Date.parse(restEndsAt);
  if (!Number.isFinite(startMs) || !Number.isFinite(endsMs)) return 0;
  return Math.max(0, Math.floor((endsMs - startMs) / 1000));
}

function firstTag(tagsByVariety, key) {
  const tags = tagsByVariety?.[key];
  if (!Array.isArray(tags) || !tags.length) return '';
  return tags[0];
}

function tagList(tagsByVariety, key) {
  const tags = tagsByVariety?.[key];
  if (!Array.isArray(tags)) return [];
  return tags;
}

/** API decimals/strings ("365.0") → whole pounds for the kiosk steppers. */
export function toWholeLb(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.round(number);
}

function sumRemainingRest(exercises) {
  return exercises.reduce((total, exercise) => {
    return (
      total +
      (exercise.planned_sets || []).reduce((setTotal, set) => {
        if (set.completed) return setTotal;
        return setTotal + (set.prescribed_rest_seconds || 0);
      }, 0)
    );
  }, 0);
}

function sumCompletedVolume(exercises) {
  return exercises.reduce((total, exercise) => {
    return (
      total +
      (exercise.planned_sets || []).reduce((setTotal, set) => {
        if (!set.completed) return setTotal;
        const weight = Number(set.actual_weight) || 0;
        const reps = Number(set.actual_reps) || 0;
        return setTotal + weight * reps;
      }, 0)
    );
  }, 0);
}

export function mapLoadedWorkout(payload, home) {
  const workout = payload?.workout || {};
  const today = home?.today_workout;
  const focusId = payload?.current_workout_exercise_id;
  const workoutExercises = workout.exercises || [];
  const source = payload?.source || 'slot';

  let focusIndex = workoutExercises.findIndex((item) => item.id === focusId);
  if (focusIndex < 0) focusIndex = 0;

  const exercises = workoutExercises.map((workoutExercise) => {
    const exercise = workoutExercise.exercise || {};
    const tags = exercise.tags_by_variety || {};

    return {
      id: workoutExercise.id,
      name: exercise.name || '',
      category: firstTag(tags, 'training_quality'),
      movement_pattern: firstTag(tags, 'movement_pattern'),
      body_region: firstTag(tags, 'body_region'),
      coaching_cues: Array.isArray(exercise.coaching_cues)
        ? exercise.coaching_cues
        : [],
      primary_muscle: firstTag(tags, 'primary_muscle'),
      secondary_muscles: tagList(tags, 'secondary_muscles'),
      tags_by_variety: tags,
      assets: Array.isArray(exercise.assets)
        ? exercise.assets.map((asset) => ({
            id: asset.id,
            variety: asset.variety,
            variety_formatted: asset.variety_formatted,
            filename_url: asset.filename_url || null,
            external_link_url: asset.external_link_url || null,
            content_type: asset.content_type || null,
          }))
        : [],
      planned_sets: (workoutExercise.sets || []).map((set) => {
        const outcome = set.outcome;
        const finished = outcome === 2 || outcome === 3;
        return {
          id: set.id,
          position: set.position,
          prescribed_weight: toWholeLb(set.prescribed_weight),
          prescribed_reps: set.prescribed_reps,
          prescribed_rest_seconds: set.prescribed_rest_seconds,
          prescribed_how: set.prescribed_how,
          completed: finished,
          actual_weight: finished ? toWholeLb(set.actual_weight) : null,
          actual_reps: finished ? set.actual_reps : null,
          rpe: finished
            ? (set.actual_rpe ?? set.estimated_rpe ?? null)
            : (set.estimated_rpe ?? null),
          set_attempt_id: set.set_attempt_id || null,
          outcome: outcome ?? null,
          started_at: set.started_at || null,
          ended_at: set.ended_at || null,
          rest_started_at: set.rest_started_at || null,
          rest_ends_at: set.rest_ends_at || null,
        };
      }),
    };
  });

  if (focusIndex < 0 || focusIndex >= exercises.length) {
    focusIndex = 0;
  }

  const focusHasOpen = (exercises[focusIndex]?.planned_sets || []).some(
    (set) => !set.completed,
  );
  if (!focusHasOpen) {
    const nextOpen = exercises.findIndex((exercise) =>
      (exercise.planned_sets || []).some((set) => !set.completed),
    );
    if (nextOpen >= 0) focusIndex = nextOpen;
  }

  return {
    session: {
      program_name:
        payload?.program_name ||
        today?.program_name ||
        workout.name ||
        'Workout',
      session_label:
        payload?.session_label || today?.session_label || workout.name || '',
      source,
      workout_slot_id: source === 'slot' ? payload?.id || null : null,
      athlete_calendar_event_id: payload?.athlete_calendar_event_id || null,
      on_date: payload?.on_date || null,
      workout_session_id: payload?.workout_session_id || null,
      session_status: payload?.session_status ?? null,
      session_status_formatted: payload?.session_status_formatted || null,
      session_stats: {
        volume_lb:
          payload?.session_stats?.volume_lb ?? sumCompletedVolume(exercises),
        elapsed_seconds: payload?.session_stats?.elapsed_seconds ?? 0,
        remaining_seconds: sumRemainingRest(exercises),
      },
      exercises,
    },
    exerciseIndex: focusIndex,
  };
}

/** @deprecated use mapLoadedWorkout */
export function mapSlotToSession(slot, home) {
  return mapLoadedWorkout(slot, home);
}

export function remainingRestSeconds(session) {
  return sumRemainingRest(session?.exercises || []);
}

export function isSessionCompleteResponse(payload) {
  return (
    payload?.session_complete === true ||
    payload?.session_status_formatted === 'completed' ||
    Number(payload?.session_status) === 2
  );
}

export function isSetFinished(set) {
  return Boolean(set?.completed) || set?.outcome === 2 || set?.outcome === 3;
}

export function isLastUnfinishedSet(session, exerciseIndex, setIndex) {
  const exercises = session?.exercises || [];
  let unfinishedCount = 0;
  let matchesTarget = false;

  exercises.forEach((exercise, exIdx) => {
    (exercise.planned_sets || []).forEach((set, setIdx) => {
      if (isSetFinished(set)) return;
      unfinishedCount += 1;
      if (exIdx === exerciseIndex && setIdx === setIndex) {
        matchesTarget = true;
      }
    });
  });

  return unfinishedCount === 1 && matchesTarget;
}

export function sessionHasOpenSets(session) {
  return (session?.exercises || []).some((exercise) =>
    (exercise.planned_sets || []).some((set) => !isSetFinished(set)),
  );
}
